import BaseController from './base_controller'

const ACTIVE_GROUP_COOKIE = 'active_vessel_group'

export default class extends BaseController {
  static targets = [
    'vessel',
    'vesselCard',
    'marketRatesBar',
    'addVesselWrapper',
    'tabPanel',
    'newVesselInput',
    'vesselsActionButtons',
    'vesselsGroupModal',
    'formsWrapper',
    'scrollNavButtons',
    'scrollStartBtn',
    'scrollEndBtn',
  ]

  connect() {
    this.element.addEventListener("calculator:showVessels", () => this.showVessels())
    this.element.addEventListener("calculator:closeVessels", () => this.closeVessels())
    this.boundPostCalculationHandler = (event) => this.handlePostCalculation(event)
    this.element.addEventListener("calculator:postCalculation", this.boundPostCalculationHandler)
    this.element.addEventListener('value-guidance:vesselRemoved', (e) => this.removeVesselByImo(e.detail?.imo))

    this.boundBackHandler = this.handleBackButton.bind(this)
    document.addEventListener('calculator:backButton', this.boundBackHandler)
    this.boundVesselCreatedHandler = (event) => this.handleVesselCreated(event)
    window.addEventListener('vessel:created', this.boundVesselCreatedHandler)

    this.manageScrollListeners(true)
  }

  disconnect() {
    document.removeEventListener('calculator:backButton', this.boundBackHandler)
    if (this.boundPostCalculationHandler) {
      this.element.removeEventListener("calculator:postCalculation", this.boundPostCalculationHandler)
    }
    window.removeEventListener('vessel:created', this.boundVesselCreatedHandler)
    this.manageScrollListeners(false)
  }

  manageScrollListeners(add) {
    const method = add ? 'addEventListener' : 'removeEventListener'

    if (add) {
      this.boundScrollHandler = (event) => this.scrollToVessel(event)
      this.boundScrollToEndHandler = () => this.scrollToFormEnd()
    }
    this.element[method]('calculator:scrollToVessel', this.boundScrollHandler)
    window[method]('calculator:scrollToEnd', this.boundScrollToEndHandler)

    if (!this.hasFormsWrapperTarget) return

    if (add) {
      this.boundHScrollHandler = () => this.updateScrollButtons()
      this.boundTabChangeHandler = () => this.updateScrollButtons()
      this.boundGroupCreatedHandler = () => {
        requestAnimationFrame(() => {
          this.formsWrapperTargets.forEach(w => {
            w.addEventListener('scroll', this.boundHScrollHandler)
            w.addEventListener('scrollend', this.boundHScrollHandler)
            this.scrollObserver?.observe(w)
          })
        })
      }
    }

    this.formsWrapperTargets.forEach(w => {
      w[method]('scroll', this.boundHScrollHandler)
      w[method]('scrollend', this.boundHScrollHandler)
    })
    window[method]('component-tab-group:tab-selected', this.boundTabChangeHandler)
    window[method]('vessel-group:created', this.boundGroupCreatedHandler)

    if (add) {
      this.scrollObserver = new ResizeObserver(() => this.updateScrollButtons())
      this.formsWrapperTargets.forEach(w => this.scrollObserver.observe(w))
      this.updateScrollButtons()
    } else {
      this.scrollObserver?.disconnect()
    }
  }

  handleBackButton(customEvent) {
    const anyVesselHidden = this.vesselTargets.some(v => v.classList.contains('d-none'))
    const admiraltyOpen = document.querySelector('[data-vessel-card-mobile-target="admiraltyContent"]:not(.d-none)') !== null

    if (anyVesselHidden || admiraltyOpen) {
      const originalEvent = customEvent.detail.originalEvent
      if (originalEvent) originalEvent.preventDefault()
      this.showVessels()
      this.vesselCardTargets.forEach(el => {
        el.dispatchEvent(new CustomEvent("vessel-card-mobile:closeVessel", { bubbles: true }))
        el.dispatchEvent(new CustomEvent("vessel-card-mobile:closeAdmiralty", { bubbles: true }))
      })
    }
  }

  closeVessels() {
    this.vesselTargets.forEach(v => v.classList.add('d-none'))
    if (this.hasMarketRatesBarTarget) {
      this.marketRatesBarTarget.classList.add('d-none')
    }
    this.hideMobileButtons()
  }

  showVessels() {
    this.vesselTargets.forEach(v => v.classList.remove('d-none'))
    if (this.hasMarketRatesBarTarget) {
      this.marketRatesBarTarget.classList.remove('d-none')
    }
    this.showMobileButtons()
  }

  hideMobileButtons() {
    if (this.hasVesselsActionButtonsTarget) {
      this.vesselsActionButtonsTarget.classList.add('d-none')
    }
  }

  showMobileButtons() {
    if (this.hasVesselsActionButtonsTarget) {
      this.vesselsActionButtonsTarget.classList.remove('d-none')
    }
  }

  // TODO: temporary until we move to vessel_card_mobile_controller
  removeVesselByImo(imo) {
    if (!imo) return
    const form = this.element.querySelector(`form[data-imo="${imo}"]`)
    if (form) form.remove()
    this.hideLoading()
  }

  scrollToVessel(event) {
    const imo = event?.detail?.imo
    if (!imo) return
    if (window.innerWidth >= 576) return

    requestAnimationFrame(() => {
      const vesselCard = this.vesselCardTargets.find(card => card.dataset.imo === imo)
      if (vesselCard) {
        vesselCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
  }

  handlePostCalculation(event) {
    const imo = event?.detail?.imo
    if (!imo) return

    const vesselCard = this.vesselCardTargets.find(card => card.dataset.imo === imo)
    if (vesselCard) {
      vesselCard.dispatchEvent(new CustomEvent("vessel-card-mobile:closeVessel", { bubbles: true }))
    }

    this.showVessels()
  }

  activeFormsWrapper() {
    if (this.hasTabPanelTarget) {
      const activePanel = this.tabPanelTargets.find(p => p.classList.contains('is-active'))
      const activePanelId = activePanel?.dataset.panel
      if (activePanelId) {
        return this.formsWrapperTargets.find(w => w.dataset.panel === activePanelId)
      }
    }
    return this.hasFormsWrapperTarget ? this.formsWrapperTarget : null
  }

  scrollToStart() {
    const wrapper = this.activeFormsWrapper()
    if (!wrapper) return
    this.dispatch('buttonScrollStart', { detail: { duration: 500 } })
    wrapper.scrollTo({ left: 0, behavior: 'smooth' })
  }

  scrollToEnd() {
    const wrapper = this.activeFormsWrapper()
    if (!wrapper) return
    this.dispatch('buttonScrollStart', { detail: { duration: 500 } })
    wrapper.scrollTo({ left: wrapper.scrollWidth, behavior: 'smooth' })
  }

  updateScrollButtons() {
    if (!this.hasScrollNavButtonsTarget) return
    const wrapper = this.activeFormsWrapper()

    if (!wrapper || wrapper.scrollWidth <= wrapper.clientWidth) {
      this.scrollNavButtonsTarget.classList.add('d-none')
      return
    }

    this.scrollNavButtonsTarget.classList.remove('d-none')

    const atStart = wrapper.scrollLeft <= 0
    const atEnd = Math.ceil(wrapper.scrollLeft) >= wrapper.scrollWidth - wrapper.clientWidth

    if (this.hasScrollStartBtnTarget) this.scrollStartBtnTarget.classList.toggle('d-none', atStart)
    if (this.hasScrollEndBtnTarget) this.scrollEndBtnTarget.classList.toggle('d-none', atEnd)
  }

  scrollToFormEnd() {
    this.dispatch('buttonScrollStart', { detail: { duration: 1000 } })
    requestAnimationFrame(() => {
      const wrapper = this.activeFormsWrapper()
      if (wrapper) {
        wrapper.scrollTo({ left: wrapper.scrollWidth, behavior: 'smooth' })
        this.updateScrollButtons()
      }
    })
  }

  async addVessel(event) {
    event.preventDefault()

    this.closeVessels()
    const url = event.currentTarget.href
    const response = await fetch(url, {
      headers: { "Accept": "text/vnd.turbo-stream.html, text/html" }
    })
    const html = await response.text()

    const addVesselWrapper = this.currentAddVesselWrapper()
    if (addVesselWrapper && !this.hasNewVesselInputTarget) {
      addVesselWrapper.insertAdjacentHTML("beforeend", html)
      window.scrollTo({ top: 0, behavior: 'smooth' })
      if (this.hasNewVesselInputTarget) {
        this.newVesselInputTarget.focus({ preventScroll: true })
      }
      this.scrollToFormEnd()
    }
  }

  currentAddVesselWrapper() {
    const activePanel = this.tabPanelTargets.find((panel) => panel.classList.contains('is-active'))
    const activePanelId = activePanel?.dataset.panel

    if (activePanelId) {
      const wrapper = this.addVesselWrapperTargets.find((target) => target.dataset.panel === activePanelId)
      return wrapper
    }

    return this.addVesselWrapperTargets[0]
  }

  handleVesselCreated(event) {
    const { vesselHtml } = event.detail
    const activeWrapper = this.activeFormsWrapper()
    if (!activeWrapper) return

    const panelId = activeWrapper.dataset.panel
    const addVesselWrapper = this.addVesselWrapperTargets.find(w => w.dataset.panel === panelId)

    if (addVesselWrapper) {
      addVesselWrapper.insertAdjacentHTML('beforebegin', vesselHtml)
    } else {
      activeWrapper.insertAdjacentHTML('beforeend', vesselHtml)
    }
  }

  openNewVesselsGroupModal() {
    if (!this.hasVesselsGroupModalTarget) return
    this.vesselsGroupModalTarget.showModal()
  }

  closeNewVesselsGroupModal() {
    if (!this.hasVesselsGroupModalTarget) return
    this.vesselsGroupModalTarget.close('vessels-group-modal')
  }
}
